import { useQuery } from '@tanstack/react-query';
import { reportsApi } from '../lib/api';

export function useStandardReport(
  siteId: string,
  range = 'last7days',
  startDate?: string,
  endDate?: string
) {
  return useQuery({
    queryKey: ['standardReport', siteId, range, startDate, endDate],
    queryFn: async () => {
      const response = await reportsApi.getStandard(siteId, range, startDate, endDate);
      return response.data;
    },
    enabled: !!siteId,
    staleTime: 1000 * 60 * 5, // 5 minutes
    refetchInterval: 1000 * 60 * 5, // Refetch every 5 minutes
  });
}

export function useEcommerceReport(
  siteId: string,
  range = 'last7days',
  startDate?: string,
  endDate?: string,
  enabled = true
) {
  return useQuery({
    queryKey: ['ecommerceReport', siteId, range, startDate, endDate],
    queryFn: async () => {
      const response = await reportsApi.getEcommerce(siteId, range, startDate, endDate);
      return response.data;
    },
    enabled: !!siteId && enabled,
    staleTime: 1000 * 60 * 5, // 5 minutes
    refetchInterval: 1000 * 60 * 5, // Refetch every 5 minutes
  });
}

export function useRealtimeReport(siteId: string) {
  return useQuery({
    queryKey: ['realtimeReport', siteId],
    queryFn: async () => {
      const response = await reportsApi.getRealtime(siteId);
      return response.data;
    },
    enabled: !!siteId,
    staleTime: 1000 * 30, // 30 seconds
    // Only auto-refresh if we have data (not blocked by plan limits or errors)
    refetchInterval: (query) => {
      if (query.state.error || !query.state.data) {
        return false; // Stop auto-refresh for errors/upgrade prompts
      }
      return 1000 * 30; // Refetch every 30 seconds when data is available
    },
    retry: false, // Don't retry on feature access errors
  });
}

export function useOverviewReport(siteId: string) {
  return useQuery({
    queryKey: ['overviewReport', siteId],
    queryFn: async () => {
      const response = await reportsApi.getOverview(siteId);
      return response.data;
    },
    enabled: !!siteId,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
}

export function useTrafficSourcesReport(
  siteId: string,
  range = 'last7days',
  startDate?: string,
  endDate?: string
) {
  return useQuery({
    queryKey: ['trafficSourcesReport', siteId, range, startDate, endDate],
    queryFn: async () => {
      const response = await reportsApi.getTrafficSources(siteId, range, startDate, endDate);
      return response.data;
    },
    enabled: !!siteId,
    staleTime: 1000 * 60 * 5, // 5 minutes
    refetchInterval: 1000 * 60 * 5, // Refetch every 5 minutes
  });
}

export function useHourlyAnalysisReport(
  siteId: string,
  range = 'last7days',
  startDate?: string,
  endDate?: string
) {
  return useQuery({
    queryKey: ['hourlyAnalysisReport', siteId, range, startDate, endDate],
    queryFn: async () => {
      const response = await reportsApi.getHourlyAnalysis(siteId, range, startDate, endDate);
      return response.data;
    },
    enabled: !!siteId,
    staleTime: 1000 * 60 * 5, // 5 minutes
    // Only auto-refresh if we have data (not blocked by plan limits or errors)
    refetchInterval: (query) => {
      if (query.state.error || !query.state.data) {
        return false; // Stop auto-refresh for errors/upgrade prompts
      }
      return 1000 * 60 * 5; // Refetch every 5 minutes when data is available
    },
    retry: false, // Don't retry on feature access errors
  });
}

export function usePagesAnalysisReport(
  siteId: string,
  range = 'last7days',
  startDate?: string,
  endDate?: string
) {
  return useQuery({
    queryKey: ['pagesAnalysisReport', siteId, range, startDate, endDate],
    queryFn: async () => {
      const response = await reportsApi.getPagesAnalysis(siteId, range, startDate, endDate);
      return response.data;
    },
    enabled: !!siteId,
    staleTime: 1000 * 60 * 5, // 5 minutes
    refetchInterval: 1000 * 60 * 5, // Refetch every 5 minutes
  });
}
